import * as React from "react"
import { Link, useLocation } from "wouter"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui-simple"
import { ScrollArea } from "@/components/ui-simple"
import { 
  LayoutDashboard, 
  FileText, 
  ShoppingCart, 
  CreditCard,
  LogOut,
  Building,
  Menu,
  X
} from "lucide-react"

// Sidebar component props: { className }

const menuItems = [
  {
    title: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard
  },
  {
    title: "Registration",
    href: "/registration-status",
    icon: FileText
  },
  {
    title: "My Purchase Orders",
    href: "/purchase-orders",
    icon: ShoppingCart
  },
  {
    title: "My Payments",
    href: "/payments",
    icon: CreditCard
  }
]

export function Sidebar({ className }) {
  const [location] = useLocation()
  const [isMobileOpen, setIsMobileOpen] = React.useState(false)

  return (
    <>
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="sm"
        className="fixed top-4 left-4 z-50 md:hidden bg-white/80 backdrop-blur-sm shadow-md"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
      >
        {isMobileOpen ? <X size={20} /> : <Menu size={20} />}
      </Button>

      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-40 w-64 bg-gradient-to-b from-white to-gray-50/50 border-r border-gray-200/60 backdrop-blur-xl transition-transform duration-300 ease-in-out",
        isMobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        className
      )}>
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex h-16 items-center gap-3 border-b border-gray-200/60 px-6">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-orange-500 rounded-lg flex items-center justify-center">
              <Building className="text-white" size={18} />
            </div>
            <div className="flex flex-col">
              <h2 className="text-sm font-semibold text-gray-900">Supplier Portal</h2>
              <p className="text-xs text-gray-500">Management System</p>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            <nav className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon
                const isActive = location === item.href
                
                return (
                  <Link key={item.href} href={item.href}>
                    <Button
                      variant={isActive ? "default" : "ghost"}
                      className={cn(
                        "w-full justify-start gap-3 h-11 px-3 rounded-xl transition-all duration-200",
                        isActive 
                          ? "bg-gradient-to-r from-blue-600 to-orange-500 text-white shadow-md hover:from-blue-700 hover:to-orange-600" 
                          : "text-gray-700 hover:text-gray-900 hover:bg-gray-100/80"
                      )}
                      onClick={() => setIsMobileOpen(false)}
                    >
                      <Icon size={18} />
                      <span className="font-medium">{item.title}</span>
                    </Button>
                  </Link>
                )
              })}
            </nav>
          </ScrollArea>

          {/* Footer */}
          <div className="border-t border-gray-200/60 p-4">
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-11 px-3 rounded-xl text-gray-700 hover:text-red-600 hover:bg-red-50/80 transition-colors"
              onClick={async () => {
                try {
                  await fetch("/api/logout", { method: "POST" });
                  window.location.href = "/";
                } catch (error) {
                  console.error("Logout error:", error);
                  window.location.href = "/";
                }
              }}
            >
              <LogOut size={18} />
              <span className="font-medium">Sign Out</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 z-30 bg-black/20 backdrop-blur-sm md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
    </>
  )
}